import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  InventoryConversionFactorUpdate,
  InventoryGroupDropdownEvent,
  InventoryItemSelectionUpdate,
  InventoryLineNumberEvent,
  InventoryPriceUpdate,
  InventoryToolTipMessaging,
  InventoryUOMDropdownEvent,
} from '@inventory/models/edit-inventory.model';
import { InventoryProductViewModel } from '@inventory/models/inventory-view.model';
import { IonInput } from '@ionic/angular';
import { PlatformEnum } from '@panamax/app-state';
import { InventoryGroupHeader } from '@usf/ngrx-inventory';

@Component({
  selector: 'app-inventory-edit-card',
  templateUrl: './inventory-edit-card.component.html',
  styleUrls: ['./inventory-edit-card.component.scss'],
})
export class InventoryEditCardComponent {
  public PlatformEnum = PlatformEnum;
  public priceIsBeingInteracted = false;

  @Input() platform: PlatformEnum;
  @Input() inventoryId: string;
  @Input() inventoryItem: InventoryProductViewModel;
  @Input() inventoryLine: number;
  @Input() groupHeaders: InventoryGroupHeader[];
  @Input() index: number;

  @Output()
  selectionEvent: EventEmitter<InventoryItemSelectionUpdate> = new EventEmitter();
  @Output()
  updateGroupEvent: EventEmitter<InventoryGroupDropdownEvent> = new EventEmitter();
  @Output()
  productDescriptionClick: EventEmitter<InventoryProductViewModel> = new EventEmitter();
  @Output()
  updateUOMEvent: EventEmitter<InventoryUOMDropdownEvent> = new EventEmitter();
  @Output()
  updatePriceOverrideEvent: EventEmitter<InventoryPriceUpdate> = new EventEmitter();
  @Output()
  updateConversionFactorEvent: EventEmitter<InventoryConversionFactorUpdate> = new EventEmitter();
  @Output()
  toolTipHoverEvent: EventEmitter<InventoryToolTipMessaging> = new EventEmitter();
  @Output()
  toolTipLeaveEvent: EventEmitter<void> = new EventEmitter();
  @Output()
  resetPreviousHotkeyIdEvent: EventEmitter<void> = new EventEmitter();
  @Output()
  itemForInformationEvent: EventEmitter<InventoryProductViewModel> = new EventEmitter();
  @Output()
  lineNumberEvent: EventEmitter<InventoryLineNumberEvent> = new EventEmitter();

  constructor() {}

  async handleSelectionEvent(ignoreThis: boolean) {
    this.selectionEvent.emit({
      inventoryId: this.inventoryId,
      inventoryItemId: this.inventoryItem?.productNumber,
      groupName: this.inventoryItem?.groupName,
      selection: !this.inventoryItem?.isSelected,
      isCatchWeight: this.inventoryItem?.productCatchWeightFlag,
    });
  }

  async handleGroupEvent(event: Event) {
    event.stopPropagation();
    const groupIndex = this.groupHeaders.find(
      value => value.groupName === this.inventoryItem.groupName,
    ).groupAbsoluteIndex;
    this.updateGroupEvent.emit({
      event,
      inventoryItemIndex: this.inventoryItem?.itemAbsoluteIndex,
      targetItemIndex: 0,
      inventoryId: this.inventoryId,
      groupIndex: groupIndex,
      groupHeaders: this.groupHeaders,
    });
  }

  async handleUOMEvent(event: Event) {
    event.stopPropagation();

    this.updateUOMEvent.emit({
      event,
      inventoryItemId: this.inventoryItem?.productNumber,
      inventoryId: this.inventoryId,
      group: this.inventoryItem?.groupName,
      isCatchWeight: this.inventoryItem?.productCatchWeightFlag,
      inventoryItem: this.inventoryItem,
    });
  }

  async handleConversionFactorEvent(event$: any) {
    let conversion = event$?.currentTarget?.value
      ? Number(event$?.currentTarget?.value).valueOf()
      : 0;

    if (conversion !== this.inventoryItem.displayConvFactor) {
      this.updateConversionFactorEvent.emit({
        inventoryId: this.inventoryId,
        inventoryItemId: this.inventoryItem?.productNumber,
        groupName: this.inventoryItem?.groupName,
        conversionValue: conversion,
      });
    }

    if (conversion === 0) {
      event$.currentTarget.value = this.inventoryItem.displayConvFactor;
    }
  }

  async handlePriceOverrideEvent(event$: any) {
    let override = event$?.currentTarget?.value
      ? Number(event$?.currentTarget?.value).valueOf()
      : 0;

    if (
      (this.inventoryItem?.overrideCasePrice !== override &&
        this.inventoryItem?.priceOverrideInd === 'Y') ||
      (this.inventoryItem?.productCasePrice !== override &&
        (this.inventoryItem?.productCasePrice !== -1 || override !== 0) &&
        this.inventoryItem?.priceOverrideInd === 'N') ||
      (this.inventoryItem.productCatchWeightFlag &&
        this.inventoryItem.unitPrice !== override)
    ) {
      this.updatePriceOverrideEvent.emit({
        inventoryId: this.inventoryId,
        inventoryItemId: this.inventoryItem?.productNumber,
        groupName: this.inventoryItem?.groupName,
        overrideValue: override,
      });
    }
    this.priceIsBeingInteracted = false;
  }

  async handleCatchWeightHover(event$: Event) {
    if (this.inventoryItem.productCatchWeightFlag) {
      this.toolTipHoverEvent.emit({
        event$: event$,
        isCatchWeightWarning: true,
      });
    }
  }

  async handleCatchWeightLeave() {
    if (this.inventoryItem.productCatchWeightFlag) {
      this.toolTipLeaveEvent.emit();
    }
  }

  async handleBadPricingHover(event$: Event) {
    this.toolTipHoverEvent.emit({
      event$: event$,
      isCatchWeightWarning: false,
    });
  }

  async handleBadPricingLeave() {
    this.toolTipLeaveEvent.emit();
  }

  handleProductDescriptionClick() {
    this.productDescriptionClick.emit(this.inventoryItem);
  }

  handlePriceFocus() {
    this.priceIsBeingInteracted = true;
    this.resetPreviousHotkeyIdEvent.emit();
  }

  handleConversionFactorFocus() {
    this.resetPreviousHotkeyIdEvent.emit();
  }

  async handleInfoIconClick() {
    this.itemForInformationEvent.emit(this.inventoryItem);
  }

  handleLineNumberChange(ionInput: IonInput) {
    this.lineNumberEvent.emit({
      inventoryId: this.inventoryId,
      inventoryItem: this.inventoryItem,
      ionInput,
    });
  }
}
